import { useCallback, useState, type ChangeEvent, type RefObject } from "react";
import { Loader2, Paperclip, Square } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Textarea } from "@/components/ui/textarea";
import { SendButton } from "@/components/SendButton";

/**
 * Props for {@link ThreadReplyComposer}.
 */
export interface ThreadReplyComposerProps {
  /**
   * Sends the composed text. Resolves `true` when the message was accepted, in
   * which case the composer clears its input; `false` leaves the draft intact.
   */
  onSend: (text: string) => Promise<boolean>;
  /** Placeholder shown when the input is empty. */
  placeholder: string;
  /** Whether a send is currently in flight (drives the send button spinner). */
  isSending: boolean;
  /** Whether an attachment upload is in flight. */
  isUploadingFile: boolean;
  /** Thread is ACTIVE — render a Stop button instead of Send. */
  isActive: boolean;
  /** Inbound message is awaiting review, blocking replies. */
  isScreeningBlocked: boolean;
  /** Stops the running agent (only shown while {@link isActive}). */
  onStop: () => void;
  /** Whether a stop has already been requested (disables the Stop button). */
  stopRequested: boolean;
  /** Opens the file picker for attachments. */
  onAttachClick: () => void;
  /** Ref for the hidden file input owned by the parent. */
  fileInputRef: RefObject<HTMLInputElement>;
  /** Handles files chosen via the hidden file input. */
  onFileSelect: (event: ChangeEvent<HTMLInputElement>) => void;
}

/**
 * The thread reply input bar (attach, textarea, send/stop). Owns its draft text
 * locally so keystrokes re-render only this small subtree instead of the entire
 * message list — the single biggest source of typing lag on large threads.
 */
export function ThreadReplyComposer({
  onSend,
  placeholder,
  isSending,
  isUploadingFile,
  isActive,
  isScreeningBlocked,
  onStop,
  stopRequested,
  onAttachClick,
  fileInputRef,
  onFileSelect,
}: ThreadReplyComposerProps) {
  const [replyMessage, setReplyMessage] = useState("");

  const handleSend = useCallback(async () => {
    const sent = await onSend(replyMessage);
    if (sent) {
      setReplyMessage("");
    }
  }, [onSend, replyMessage]);

  const inputDisabled =
    isSending || isUploadingFile || isActive || isScreeningBlocked;
  const sendDisabled =
    !replyMessage.trim() || isSending || isUploadingFile || isScreeningBlocked;
  const attachDisabled =
    isUploadingFile || isSending || isActive || isScreeningBlocked;

  return (
    <div className="flex gap-2">
      <input
        ref={fileInputRef}
        type="file"
        multiple
        onChange={onFileSelect}
        className="hidden"
        aria-label="Upload file attachment"
      />
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onAttachClick}
              disabled={attachDisabled}
              className="shrink-0"
              aria-label="Attach file"
            >
              {isUploadingFile ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Paperclip className="h-4 w-4" />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent>
            <p>Attach file</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <Textarea
        value={replyMessage}
        onChange={(e) => setReplyMessage(e.target.value)}
        onSend={handleSend}
        placeholder={placeholder}
        disabled={inputDisabled}
        className="flex-1"
      />
      {isActive ? (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                onClick={onStop}
                disabled={stopRequested}
                size="icon"
                variant="outline"
              >
                {stopRequested ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Square className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Stop the agent</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <SendButton
          onClick={handleSend}
          disabled={sendDisabled}
          loading={isSending}
        />
      )}
    </div>
  );
}
